import { navigate } from "gatsby";
// biome-ignore lint/correctness/noUnusedImports: React is required for JSX
import React from "react";

// Font faces are declared in src/assets/scss/_fonts.scss - see the note there.

import abTests from "./netlify/edge-functions/ab-tests.json";
import { StrapiDataProvider } from "./src/context/StrapiDataContext";
import { normalizePath, resolveVariant } from "./src/utils/ab";
import { getCookie, setCookie } from "./src/utils/cookies";

export const wrapPageElement = ({ element, props }) => (
  <StrapiDataProvider pageContext={props.pageContext}>{element}</StrapiDataProvider>
);

const AB_SOURCE_INDEX = new Map();
for (const [sourcePath, test] of Object.entries(abTests)) {
  AB_SOURCE_INDEX.set(normalizePath(sourcePath).toLowerCase(), test);
}

const resolveAbTarget = (pathname) => {
  if (!pathname) return null;
  const hit = AB_SOURCE_INDEX.get(normalizePath(pathname).toLowerCase());
  if (!hit) return null;
  const existing = getCookie(hit.cookie);
  const { variantId, isNew } = resolveVariant(hit, existing);
  if (isNew) {
    setCookie(hit.cookie, variantId, hit.ttlDays * 24 * 30);
  }
  const variant = hit.variants.find((v) => v.id === variantId);
  if (!variant) return null;
  return variant.to;
};

export const onClientEntry = () => {
  if (typeof window === "undefined") return;
  // `window.___navigate` is not yet installed at this point, so we cannot use
  // gatsby's `navigate`. A hard replace is also semantically right: it routes
  // through the edge function, so the cookie/attribution stays consistent.
  const target = resolveAbTarget(window.location.pathname);
  if (target) {
    window.location.replace(target + window.location.search + window.location.hash);
  }
};

export const onPreRouteUpdate = ({ location }) => {
  if (typeof window === "undefined") return;
  // For SPA transitions gatsby's navigator is installed - use it so the back
  // button does not return the user to the source page.
  const target = resolveAbTarget(location?.pathname);
  if (target) {
    navigate(target + (location?.search || "") + (location?.hash || ""), { replace: true });
  }
};

const abFired = new Set();

export const onRouteUpdate = ({ location }) => {
  if (typeof window === "undefined") return;

  for (const test of Object.values(abTests)) {
    const variant = test.variants.find((v) => v.to === location.pathname);
    if (!variant) continue;

    const cookieValue = getCookie(test.cookie);
    if (!cookieValue) continue;

    const key = `${test.id}:${cookieValue}:${location.pathname}`;
    if (abFired.has(key)) continue;
    abFired.add(key);

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: "ab_test_assignment",
      ab_test_id: test.id,
      ab_variant: cookieValue,
    });
  }
};
